import { loadImageForCanvas } from '@/shared/lib/utils/cors-image-loader';
import { createLogger } from '@/shared/lib/logger';
import { calculateConstrainedDimensions } from '../drawing.utils';
import {
  getCurrentSessionId,
  setCurrentSessionId,
  getInitializedCanvasKey,
  setInitializedCanvasKey,
  setCanvasRef,
  getCanvasRef,
  setContextRef,
  getContextRef,
} from '../drawing.refs';
import type { DrawingStateCreator, CanvasSlice } from './types';

const logger = createLogger('DrawingStore');

export const createCanvasSlice: DrawingStateCreator<CanvasSlice> = (
  set,
  get
) => ({
  canvas: {
    width: 0,
    height: 0,
    transform: {
      zoom: 1,
      pan: { x: 0, y: 0 },
    },
    initialImageData: null,
    hasChanges: false,
    angleImage: null,
    angleImageUrl: null,
  },

  drawing: {
    isDrawing: false,
    lastPoint: null,
    lastDrawnPoint: null,
    isErasing: false,
    isPanning: false,
    panStart: null,
  },

  ui: {
    loading: {
      image: false,
      save: false,
      export: false,
      import: false,
    },
    error: {
      image: null,
      save: null,
      export: null,
      import: null,
    },
    showBrushPreview: false,
    showBrushSizeOnCursor: false,
    brushSliderValue: 23, // Corresponds to default brush size 7px
    showGrid: false,
    gridSize: 20,
    snapToGrid: false,
  },

  initCanvas: async (canvas, angleImageUrl, initialControlUrl) => {
    // Create a unique key for this canvas configuration
    const canvasKey = `${angleImageUrl}|${initialControlUrl || 'none'}`;

    // Prevent re-initialization of the same canvas
    // This happens in dev mode due to React Strict Mode and mobile keyboard opening
    const currentSession = getCurrentSessionId();
    if (getInitializedCanvasKey() === canvasKey && currentSession) {
      logger.info('initCanvas SKIPPED - already initialized', {
        canvasKey,
        currentSessionId: currentSession,
      });
      return;
    }

    logger.info('initCanvas called', {
      angleImageUrl,
      initialControlUrl,
      canvasKey,
      previousKey: getInitializedCanvasKey(),
      stack: new Error().stack?.split('\n').slice(0, 5).join('\n'),
    });

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('Failed to get canvas context');

    // Generate session ID and store DOM refs
    const sessionId = `drawing-${Date.now()}-${Math.random()}`;
    setCurrentSessionId(sessionId);
    setCanvasRef(sessionId, canvas);
    setContextRef(sessionId, ctx);

    set((state) => {
      state.ui.loading.image = true;
      state.ui.error.image = null;
    });

    try {
      // Load angle image (backdrop). When there is no backdrop (clean schema),
      // the canvas falls back to a default size and no overlay is shown.
      const angleImg = angleImageUrl
        ? await loadImageForCanvas(angleImageUrl)
        : null;
      let controlImg: HTMLImageElement | null = null;

      // Default canvas for a backdrop-less sketch (4:3, ~1 MP budget).
      const DEFAULT_WIDTH = 1024;
      const DEFAULT_HEIGHT = 768;

      // Calculate constrained dimensions (max 1 megapixel)
      const constrainedDims = angleImg
        ? calculateConstrainedDimensions(angleImg.width, angleImg.height)
        : { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT, isConstrained: false };
      const width = constrainedDims.width;
      const height = constrainedDims.height;

      // Log if image was constrained (only possible when a backdrop exists)
      if (angleImg && constrainedDims.isConstrained) {
        logger.info(
          `Canvas size constrained from ${angleImg.width}×${angleImg.height} (${angleImg.width * angleImg.height} pixels) to ${width}×${height} (${width * height} pixels)`
        );
      }

      canvas.width = width;
      canvas.height = height;

      // Setup context
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      // DON'T fill with white - canvas should be transparent for overlay to show through
      // White background will be added via CSS

      // Load initial control if provided
      if (initialControlUrl) {
        controlImg = await loadImageForCanvas(initialControlUrl);

        // Configure context to prevent pixel degradation during image loading
        ctx.save();
        ctx.imageSmoothingEnabled = false; // Disable smoothing to preserve sharp pixels
        ctx.globalCompositeOperation = 'copy'; // Replace pixels instead of blending
        ctx.globalAlpha = 1.0; // Full opacity to prevent alpha blending issues

        // Scale control image to match constrained canvas size
        ctx.drawImage(controlImg, 0, 0, width, height);

        ctx.restore();
      }

      // Save initial state
      const imageData = ctx.getImageData(0, 0, width, height);

      set((state) => {
        state.canvas.width = width;
        state.canvas.height = height;
        state.canvas.initialImageData = imageData;
        state.canvas.hasChanges = false;
        // @ts-expect-error - Immer cannot proxy DOM objects, but we need to store the image for overlay
        state.canvas.angleImage = angleImg; // Save blob image for canvas operations (null = no backdrop)
        state.canvas.angleImageUrl = angleImageUrl; // Save direct URL for overlay display (null = no backdrop)

        state.history.states = [
          { imageData, timestamp: Date.now(), action: 'Initial' },
        ];
        state.history.currentIndex = 0;
        state.history.canUndo = false;
        state.history.canRedo = false;

        state.ui.loading.image = false;

        // Clear text blocks when initializing new canvas (new control instance)
        state.textBlocks.blocks = [];
        state.textBlocks.activeBlockId = null;

        // Start session with the generated session ID
        state.session = {
          id: sessionId,
          startTime: new Date(),
          angleImageUrl,
          initialControlUrl,
          canvasWidth: width,
          canvasHeight: height,
        };
      });

      // DON'T cleanup angleImg blob URL - it's used for overlay
      // It will be cleaned up in endSession

      // Cleanup control image blob URL after use
      if (
        controlImg &&
        controlImg.src &&
        controlImg.src.startsWith('blob:')
      ) {
        URL.revokeObjectURL(controlImg.src);
      }

      // Mark canvas as initialized to prevent re-initialization
      setInitializedCanvasKey(canvasKey);
      logger.info('Canvas initialized successfully', canvasKey);
    } catch (error) {
      set((state) => {
        state.ui.loading.image = false;
        state.ui.error.image =
          error instanceof Error ? error.message : 'Failed to load images';
      });
      throw error;
    }
  },

  setBackdrop: (angleImageUrl) =>
    set((state) => {
      // Overlay-only swap: the drawing canvas (strokes) is independent of the
      // backdrop guide, so changing/removing the backdrop never touches the
      // drawing or canvas dimensions. Drop the cached blob image; the overlay
      // <img> renders straight from the URL.
      state.canvas.angleImageUrl = angleImageUrl;
      state.canvas.angleImage = null;
    }),

  clearCanvas: () =>
    set((state) => {
      const { width, height } = state.canvas;
      const contextRef = getContextRef();
      if (!contextRef) return;

      // Clear to transparent (not white) so overlay shows through
      contextRef.clearRect(0, 0, width, height);

      get().saveToHistory('Clear');
    }),

  resetCanvas: () =>
    set((state) => {
      const { initialImageData } = state.canvas;
      const contextRef = getContextRef();
      if (!contextRef || !initialImageData) return;

      contextRef.putImageData(initialImageData, 0, 0);
      state.canvas.hasChanges = false;

      // Clear all text blocks when resetting
      state.textBlocks.blocks = [];
      state.textBlocks.activeBlockId = null;

      get().saveToHistory('Reset');
    }),

  setCanvasTransform: (transform) =>
    set((state) => {
      Object.assign(state.canvas.transform, transform);
    }),

  zoomIn: () =>
    set((state) => {
      state.canvas.transform.zoom = Math.min(
        state.canvas.transform.zoom * 1.5,
        5
      );
    }),

  zoomOut: () =>
    set((state) => {
      state.canvas.transform.zoom = Math.max(
        state.canvas.transform.zoom / 1.5,
        0.5
      );
    }),

  resetZoom: () =>
    set((state) => {
      state.canvas.transform.zoom = 1;
      state.canvas.transform.pan = { x: 0, y: 0 };
    }),

  fitToScreen: () => {
    // Not implemented - would require container dimensions
    // Kept for potential future implementation
  },

  screenToCanvas: (screenPoint) => {
    const { transform } = get().canvas;
    return {
      x: (screenPoint.x - transform.pan.x) / transform.zoom,
      y: (screenPoint.y - transform.pan.y) / transform.zoom,
    };
  },

  canvasToScreen: (canvasPoint) => {
    const { transform } = get().canvas;
    return {
      x: canvasPoint.x * transform.zoom + transform.pan.x,
      y: canvasPoint.y * transform.zoom + transform.pan.y,
    };
  },

  setShowBrushPreview: (show) =>
    set((state) => {
      state.ui.showBrushPreview = show;
    }),

  setShowBrushSizeOnCursor: (show) =>
    set((state) => {
      state.ui.showBrushSizeOnCursor = show;
    }),

  setShowGrid: (show) =>
    set((state) => {
      state.ui.showGrid = show;
    }),

  setGridSize: (size) =>
    set((state) => {
      state.ui.gridSize = size;
    }),

  setSnapToGrid: (snap) =>
    set((state) => {
      state.ui.snapToGrid = snap;
    }),

  clearErrors: (key) =>
    set((state) => {
      if (key) {
        state.ui.error[key] = null;
      } else {
        state.ui.error = {
          image: null,
          save: null,
          export: null,
          import: null,
        };
      }
    }),

  getCanvasRef,
  getContextRef,
});
